import React from 'react';
import Html from 'components/html';
import { useIntl } from 'react-intl';
import NavigationDots from 'components/navigation-dots';
import { sendTracking } from 'services/google-analytics';

import tracking from './start-earning.tracking';
import { slides, testId, navItems } from './start-earning.settings';
import styles from './start-earning.module.scss';
import i18n from './start-earning.i18n';
import DesktopCarousel, { type DesktopCarouselHandle } from './desktop-carousel/desktop-carousel';
import MobileCarousel from './mobile-carousel/mobile-carousel';

const StartEarning: React.FunctionComponent<unknown> = () => {
  const { formatMessage } = useIntl();
  const desktopCarouselRef = React.useRef<DesktopCarouselHandle>(null);
  const [slide, setSlide] = React.useState(0);
  const translation = i18n.pages.landing.startEarning.livejasmin;

  const setCarouselSlide = React.useCallback(
    (slideIndex: number) => () => {
      sendTracking(tracking.navMenu, { id: String(navItems[slideIndex].id) });
      setSlide(slideIndex);
      desktopCarouselRef.current?.setActiveSlide(slideIndex);
    },
    []
  );

  return (
    <Html.div className={[styles.container, 'w-100 text-center pb-12 pb-md-16']} testId={testId.component}>
      <Html.h2 className={[styles.title, 'mb-4 px-2']} fontWeight="light">
        {formatMessage(translation.title, {
          b: (contents: React.ReactNode) => (
            <Html.b className={styles.titleHighlight} fontWeight="bold">
              {contents}
            </Html.b>
          ),
        })}
      </Html.h2>
      <Html.div>
        <Html.p className="m-0">{formatMessage(translation.description1)}</Html.p>
        <Html.p className="m-0">
          <Html.span className="d-none d-md-inline">{formatMessage(translation.description2)}</Html.span>{' '}
          {formatMessage(translation.description3)}
        </Html.p>
      </Html.div>

      <Html.div className="d-block d-md-none mt-4">
        <Html.ul className={['mb-3', styles.label]} typography="title1" fontWeight="medium">
          {navItems.map((item) => (
            <Html.li
              className={[styles.labelItem, slide === item.id && styles.labelItemActive]}
              key={`label${item.id}`}
            >
              {formatMessage(item.label)}
            </Html.li>
          ))}
        </Html.ul>

        <MobileCarousel slides={slides} onSlideChange={setSlide} />

        <Html.div className="d-flex justify-content-center mt-4">
          <NavigationDots total={slides.length} activeIndex={slide} theme="dark" />
        </Html.div>
      </Html.div>

      <Html.div className="d-none d-md-flex mt-6">
        <Html.div className={['text-start mb-6 pe-4', styles.navContainer]}>
          <Html.ul className={[styles.carouselNav]}>
            {navItems.map((item) => (
              <Html.li
                className={['p-6', styles.carouselNavItem, slide === item.id && styles.carouselNavItemActive]}
                onClick={setCarouselSlide(item.id)}
                key={`label${item.id}`}
                testId={testId.carouselNav}
              >
                {formatMessage(item.label)}
              </Html.li>
            ))}
          </Html.ul>
        </Html.div>
        <DesktopCarousel slides={slides} ref={desktopCarouselRef} />
      </Html.div>
    </Html.div>
  );
};

export default StartEarning;
